<script setup lang="ts">
// NPM
import { onMounted, ref, watch } from 'vue';

// Components
import { FieldLabel } from '@magma-app/magma-lapilli';

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  id: {
    type: [String, Number],
    default: null,
  },
  label: {
    type: String,
    default: null,
  },
  description: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  rows: {
    type: Number,
    default: 4,
  },
  autocomplete: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  validation: {
    type: Object,
    default: null,
  },
});

// Reactive variables
const root = ref<HTMLInputElement>();
const focused = ref(false);

// Event emitters
const emit = defineEmits([
  'blur',
  'change',
  'focus',
  'submit',
  'update:modelValue',
]);

// Lifecycle hooks
onMounted(() => updateTextInput());

// Watchers
watch(
  () => props.modelValue,
  () => {
    updateTextInput();
  }
);

// Event listeners
const onTextFocus = (event: Event) => {
  const target = event.target as HTMLInputElement;

  focused.value = true;

  emit('focus', target.value, props.id);
};

const onTextBlur = (event: Event) => {
  const target = event.target as HTMLInputElement;

  focused.value = false;

  emit('blur', target.value, props.id);
};

const onTextInput = (event: Event) => {
  const target = event.target as HTMLInputElement;

  const value = target.value;

  emit('change', value, props.id);
  emit('update:modelValue', value, props.id);
};

const onTextKeypress = (event: KeyboardEvent) => {
  if (event.code == 'Enter') {
    emit('submit', props.id);
  }
};

// Methods
const updateTextInput = () => {
  if (root.value) {
    const inputElement = root.value.querySelector('input');

    if (inputElement) {
      inputElement.value = props.modelValue;
    }
  }
};
</script>

<template>
  <div ref="root">
    <div v-if="label" class="mb-1.5">
      <div class="flex items-start">
        <FieldLabel :id="`${id}`" :label="label" />

        <span
          v-if="required"
          class="ml-1 text-mgm-txt-sm font-medium text-foreground-brand-default"
        >
          *
        </span>
      </div>

      <span
        v-if="description"
        class="block text-mgm-txt-sm font-normal text-foreground-default"
      >
        {{ description }}
      </span>
    </div>

    <textarea
      :id="`${id}`"
      class="box-border block max-h-60 w-full rounded-[4.16px] px-2.5 py-[9px] text-mgm-txt-sm font-normal text-foreground-emphasis shadow-[0px_1px_2px_rgba(28,31,39,0.05)] outline-0 placeholder:text-foreground-subtle"
      :class="[
        validation?.$errors?.length
          ? 'border-[0.68px] border-border-danger-emphasis focus:border-border-danger-emphasis focus:shadow-[0px_1px_2px_rgba(28,31,39,0.05)] focus:ring-border-danger-emphasis'
          : 'border-[0.83px] border-border-default focus:border-border-brand-emphasis focus:shadow-[0px_1px_2px_rgba(16,24,40,0.05),0px_0px_0px_2px_#C7D7FE] focus:ring-border-brand-emphasis'
      ]"
      :autocomplete="autocomplete"
      :disabled="disabled"
      :name="`${id}`"
      :placeholder="placeholder"
      :required="required"
      :rows="rows"
      @blur="onTextBlur"
      @focus="onTextFocus"
      @input="onTextInput"
      @keypress="onTextKeypress"
    />

    <FieldLabel
      v-if="validation && validation.$errors?.length"
      class="mt-1"
      :label="validation.$errors[0]?.$message"
      error
    />
  </div>
</template>
